import { GetterTree } from 'vuex';
import _ from 'lodash';
import { decimal, required, between, requiredIf, numeric } from 'vuelidate/lib/validators';

import Parameters from 'dc-engine/src/parameters';
import Cost from 'dc-engine/src/model/cost';
import EmployerMatchTable from 'dc-engine/src/tables/employerMatchTable';

import { IState, Output } from '../types';
import { formatters } from '../vs-props';

export function monthlyValueToFrequency(value: number, frequency: number, precision = 0) {
  const total = (value * 12) / frequency;
  return precision === 0 ? Math.round(total) : total;
}

export default {
  age(state) {
    const { constants, inputs, features } = state;

    const range = {
      min: [constants.MIN_AGE, 1],
      max: constants.MAX_AGE,
    };

    return {
      value: inputs.age,
      range,
      formatter: formatters.number(0),
      validation: {
        numeric,
        required: requiredIf(() => features.ageMatching),
        between: between(range.min[0], range.max),
      },
    };
  },
  income(state) {
    const { constants, ui, inputs } = state;

    const range = {
      min: [constants.MIN_INCOME, 1],
      max: constants.MAX_INCOME,
    };

    return {
      value: inputs.income,
      range,
      formatter: formatters.currency(ui.precision.currency),
      validation: {
        decimal,
        required,
        between: between(range.min[0], range.max),
      },
    };
  },
  payFrequency(state) {
    const { constants, inputs } = state;

    const options = constants.PAY_FREQUENCIES;

    return {
      value: inputs.payFrequency,
      formatter: formatters.number(0),
      options,
      validation: {
        between: between(options[options.length - 1].value, options[0].value),
      },
    };
  },
  maxOutputTotal(state, getters) {
    const { outputs } = getters;

    return outputs.length > 0 ? outputs[outputs.length - 1].total : 0;
  },
  p(state) {
    const { assumptions, inputs } = state;

    return new Parameters({
      person: {
        age: inputs.age,
        contributionRate: inputs.contributionRate,
      },
      model: {
        calculationDate: assumptions.calculationDate,
        applyScale: true,
      },
      employerMatch: assumptions.employerMatch,
    });
  },
  employerMatchTable(state, getters) {
    return new EmployerMatchTable(getters.p);
  },
  cost(state, getters) {
    return new Cost(getters.employerMatchTable);
  },
  outputs(state, getters) {
    const { assumptions, features, ui, inputs } = state;
    const { cost, employerMatchTable } = getters;
    const { salarySacrifice } = assumptions;
    const { contributionRate, income, payFrequency } = inputs;

    return _.chain(assumptions.contributionRates)
      .filter((rate) => {
        return contributionRate > 0 ? rate >= contributionRate : true;
      })
      .map((rate): Output => {
        return {
          employee: {
            rate,
            contribution: monthlyValueToFrequency(
              cost.employeeContribution(rate, income).getValue(),
              payFrequency,
              ui.precision.currency,
            ),
          },
          employer: {
            rate: employerMatchTable.getMatch(rate),
            contribution: monthlyValueToFrequency(
              cost.employerContribution(rate, income).getValue(),
              payFrequency,
              ui.precision.currency,
            ),
          },
          employerMatch: features.employerMatch,
          visible: ui.visibleRates.indexOf(rate) >= 0,
          netCost: monthlyValueToFrequency(
            cost.netCost(rate, income, salarySacrifice).getValue(),
            payFrequency,
            ui.precision.currency,
          ),
          total: monthlyValueToFrequency(
            cost.totalInvested(rate, income).getValue(),
            payFrequency,
            ui.precision.currency,
          ),
        };
      }).value();
  },
} as GetterTree<IState, any>;
